import React, { useState, useEffect, useRef } from "react";
import { parseISO, format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
// import AsyncSelect from "../AsyncSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		disableBot: false,
		lgpdAcceptedAt: "",
		document: "",
		system: "",
		domain: ""
	};

	const [contact, setContact] = useState(initialState);
	const [disableBot, setDisableBot] = useState(false);
	const [siblingContacts, setSiblingContacts] = useState([]);
	const [linkOptions, setLinkOptions] = useState([]);
	const [linkSearch, setLinkSearch] = useState("");
	const [linking, setLinking] = useState(false);
	const [linkTarget, setLinkTarget] = useState(null);
	const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
	const [useNumberFromMerge, setUseNumberFromMerge] = useState(false);
	const [merging, setMerging] = useState(false);
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Busca por nome, nao por CNPJ - na pratica o agente reconhece o cliente
	// pelo nome ("e o mesmo da Passarela Fashion"), nao pelo documento, entao
	// vincular via CNPJ sozinho quase nunca era usado.
	useEffect(() => {
		if (!linkSearch || linkSearch.length < 2) {
			setLinkOptions([]);
			return;
		}
		let active = true;
		const timer = setTimeout(async () => {
			try {
				const { data } = await api.get("/contacts/list", { params: { name: linkSearch } });
				if (active) {
					setLinkOptions((data || []).filter(c => c.id !== contactId));
				}
			} catch (err) {
				toastError(err);
			}
		}, 400);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [linkSearch, contactId]);

	const handleLinkToContact = async (targetContact) => {
		if (!targetContact || !contactId) return;
		setLinking(true);
		try {
			await api.put(`/contacts/${contactId}/link-to/${targetContact.id}`);
			toast.success("Contato vinculado!");
			const { data: orgData } = await api.get(`/contacts/${contactId}/organization-contacts`);
			setSiblingContacts(orgData.contacts || []);
			setLinkSearch("");
			setLinkOptions([]);
			setLinkTarget(null);
		} catch (err) {
			toastError(err);
		} finally {
			setLinking(false);
		}
	};

	// Mesclagem de verdade (nao so' vincular a mesma empresa) - pedido do
	// usuario 2026-08-29 depois do caso real da "Aparecida" (bug do LID
	// criando dois contatos pra mesma pessoa) ter quase custado uma venda,
	// e ele ter que ser corrigido na mao direto no banco. Transfere todo o
	// historico (tickets, mensagens, agendamentos, tags...) pro contato
	// atual e apaga o outro - ver MergeContactsService no backend.
	const handleMergeContacts = async () => {
		if (!linkTarget || !contactId) return;
		setMerging(true);
		try {
			const { data } = await api.put(`/contacts/${contactId}/merge-with/${linkTarget.id}`, {
				useNumberFromMerge,
			});
			toast.success("Contatos mesclados!");
			setContact(prev => ({ ...prev, ...data.contact }));
			setMergeDialogOpen(false);
			setLinkSearch("");
			setLinkOptions([]);
			setLinkTarget(null);
			setUseNumberFromMerge(false);
		} catch (err) {
			toastError(err);
		} finally {
			setMerging(false);
		}
	};

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...data,
						document: data.contactOrganization?.documentNumber || "",
						system: data.contactOrganization?.system || "",
						domain: data.contactOrganization?.domain || "",
					});
					setDisableBot(data.disableBot)
				}
				const { data: orgData } = await api.get(`/contacts/${contactId}/organization-contacts`);
				if (isMounted.current) setSiblingContacts(orgData.contacts || []);
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		const { document: documentNumber, system, domain, ...contactValues } = values;
		try {
			let savedId = contactId;
			if (contactId) {
				await api.put(`/contacts/${contactId}`, { ...contactValues, disableBot: disableBot });
			} else {
				const { data } = await api.post("/contacts", { ...contactValues, disableBot: disableBot });
				savedId = data.id;
				if (onSave) {
					onSave(data);
				}
			}
			if (savedId) {
				const { data: docData } = await api.put(`/contacts/${savedId}/document`, { documentNumber });
				if (docData.organization?.id) {
					await api.put(`/contact-organizations/${docData.organization.id}`, { system, domain });
				}
			}
			handleClose();
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.number")}
									name="number"
									error={touched.number && Boolean(errors.number)}
									helperText={touched.number && errors.number}
									placeholder="5513912344321"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label="CNPJ / CPF"
										name="document"
										placeholder="Somente números"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								{values.document && (
									<Box display="flex" gap={1}>
										<Field
											as={TextField}
											label="Sistema"
											name="system"
											placeholder="Qual sistema o cliente usa"
											fullWidth
											margin="dense"
											variant="outlined"
										/>
										<Field
											as={TextField}
											label="Domínio"
											name="domain"
											placeholder="dominio.com.br"
											fullWidth
											margin="dense"
											variant="outlined"
										/>
									</Box>
								)}
								{siblingContacts.length > 0 && (
									<div style={{ marginTop: 4, marginBottom: 8 }}>
										<Typography variant="caption" color="textSecondary">
											Outros contatos desta empresa: {siblingContacts.map(c => c.name).join(", ")}
										</Typography>
									</div>
								)}
								{contactId && (
									<div style={{ marginTop: 4, marginBottom: 8 }}>
										<Autocomplete
											size="small"
											options={linkOptions}
											value={linkTarget}
											inputValue={linkSearch}
											loading={linking}
											filterOptions={(x) => x}
											getOptionLabel={(option) => option.name ? `${option.name} (${option.number || ""})` : ""}
											onInputChange={(e, value) => setLinkSearch(value)}
											onChange={(e, value) => setLinkTarget(value)}
											renderInput={(params) => (
												<TextField
													{...params}
													variant="outlined"
													label="Vincular ou mesclar com um contato já conhecido"
													placeholder="Digite o nome do cliente/empresa"
													helperText="Use quando o mesmo cliente aparece com outro número (ex: bug do LID)"
												/>
											)}
										/>
										{linkTarget && (
											<Box display="flex" gap={1} mt={1}>
												<Button size="small" disabled={linking} onClick={() => handleLinkToContact(linkTarget)}>
													Só vincular à mesma empresa
												</Button>
												<Button size="small" color="secondary" onClick={() => setMergeDialogOpen(true)}>
													Mesclar contatos...
												</Button>
											</Box>
										)}
									</div>
								)}
								<div>
									<TagsContainer contact={contact} className={classes.textField} />
								</div>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									<Switch
										size="small"
										checked={disableBot}
										onChange={() =>
											setDisableBot(!disableBot)
										}
										name="disableBot"
									/>
									{i18n.t("contactModal.form.chatBotContact")}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : ""}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.termsLGDP")} {contact?.lgpdAcceptedAt ? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm") : ""}
								</Typography>

								{/* <Typography variant="subtitle1" gutterBottom>{i18n.t("contactModal.form.customer_portfolio")}</Typography> */}
								{/* <div style={{ marginTop: 10 }}>
									<AsyncSelect url="/users" dictKey={"users"}
										initialValue={values.user} width="100%" label={i18n.t("contactModal.form.attendant")}
										onChange={(event, value) => setFieldValue("userId", value ? value.id : null)} />
								</div>
								<div style={{ marginTop: 10 }}>
									<AsyncSelect url="/queue" dictKey={null}
										initialValue={values.queue} width="100%" label={i18n.t("contactModal.form.queue")}
										onChange={(event, value) => setFieldValue("queueId", value ? value.id : null)} />
								</div> */}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>

			<Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Mesclar contatos</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" gutterBottom>
						Todo o histórico de <b>{linkTarget?.name}</b> (tickets, mensagens, notas, agendamentos,
						compromissos e tags) vai passar para este contato, e o registro de{" "}
						<b>{linkTarget?.name}</b> será apagado. Essa ação não pode ser desfeita.
					</Typography>
					{linkTarget?.number && (
						<FormControlLabel
							control={
								<Checkbox
									checked={useNumberFromMerge}
									onChange={(e) => setUseNumberFromMerge(e.target.checked)}
								/>
							}
							label={`Usar o número de ${linkTarget.name} (${linkTarget.number}) neste contato`}
						/>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setMergeDialogOpen(false)} disabled={merging}>
						Cancelar
					</Button>
					<Button color="secondary" variant="contained" onClick={handleMergeContacts} disabled={merging}>
						{merging ? "Mesclando..." : "Mesclar"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ContactModal;
